import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Segment,
  Header,
  Icon,
  Dropdown,
  Form,
  Button,
  Label,
  Message,
  Loader,
  Grid,
  Table,
  Divider,
} from "semantic-ui-react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase/firebaseConfig";
import Alerta from "./Alertas";

const ContratoMantencion = () => {
  const user = auth.currentUser;

  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");

  const [tipos, setTipos] = useState([]);
  const [tiposCubiertos, setTiposCubiertos] = useState([]);
  const [mantencionesPorTipo, setMantencionesPorTipo] = useState({});

  const [activo, setActivo] = useState(true);
  const [observacion, setObservacion] = useState("");

  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [loadingContrato, setLoadingContrato] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [alerta, cambiarAlerta] = useState({});
  const [estadoAlerta, cambiarEstadoAlerta] = useState(false);

  useEffect(() => {
    const cargarEmpresas = async () => {
      try {
        setLoadingEmpresas(true);

        const snap = await getDocs(collection(db, "empresas"));

        const data = snap.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }));

        data.sort((a, b) =>
          String(a.empresa || "").localeCompare(String(b.empresa || ""))
        );

        setEmpresas(data);

        if (data.length > 0) {
          setEmpresaId(data[0].id);
        }
      } catch (error) {
        console.error("Error cargando empresas:", error);
      } finally {
        setLoadingEmpresas(false);
      }
    };

    cargarEmpresas();
  }, []);

  useEffect(() => {
    const cargarDatosEmpresa = async () => {
      if (!empresaId) return;

      try {
        setLoadingContrato(true);

        await cargarTipos(empresaId);
        await cargarMantencionesActivas(empresaId);
        await cargarContrato(empresaId);
      } catch (error) {
        console.error("Error cargando contrato:", error);
      } finally {
        setLoadingContrato(false);
      }
    };

    cargarDatosEmpresa();
  }, [empresaId]);

  const empresaSeleccionada = useMemo(() => {
    return empresas.find((item) => item.id === empresaId) || null;
  }, [empresas, empresaId]);

  const opcionesEmpresas = empresas.map((empresa) => ({
    key: empresa.id,
    value: empresa.id,
    text: empresa.empresa || "Sin nombre",
  }));

  const cargarTipos = async (empId) => {
    const q = query(collection(db, "tipos"), where("emp_id", "==", empId));
    const snap = await getDocs(q);

    const data = snap.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    data.sort((a, b) => {
      const familiaA = String(a.familia || a.familiar || "").toUpperCase();
      const familiaB = String(b.familia || b.familiar || "").toUpperCase();
      const tipoA = String(a.tipo || a.nombre || "").toUpperCase();
      const tipoB = String(b.tipo || b.nombre || "").toUpperCase();

      if (familiaA !== familiaB) {
        return familiaA.localeCompare(familiaB);
      }

      return tipoA.localeCompare(tipoB);
    });

    setTipos(data);
  };

  const cargarMantencionesActivas = async (empId) => {
    const q = query(
      collection(db, "mantenciones"),
      where("emp_id", "==", empId),
      where("activo", "==", true)
    );

    const snap = await getDocs(q);

    const resumen = {};

    snap.docs.forEach((docItem) => {
      const data = docItem.data();
      const tipo = data.tipo;

      if (!tipo) return;

      if (!resumen[tipo]) {
        resumen[tipo] = {
          cantidad: 0,
          programas: new Set(),
        };
      }

      resumen[tipo].cantidad += 1;

      if (data.programa) {
        resumen[tipo].programas.add(data.programa);
      }
    });

    const normalizado = {};

    Object.keys(resumen).forEach((tipo) => {
      normalizado[tipo] = {
        cantidad: resumen[tipo].cantidad,
        programas: Array.from(resumen[tipo].programas),
      };
    });

    setMantencionesPorTipo(normalizado);
  };

  const cargarContrato = async (empId) => {
    const q = query(
      collection(db, "contratos_mantencion"),
      where("emp_id", "==", empId),
      where("actual", "==", true)
    );

    const snap = await getDocs(q);

    if (snap.empty) {
      setTiposCubiertos([]);
      setActivo(true);
      setObservacion("");
      return;
    }

    const contrato = snap.docs[0].data();

    setTiposCubiertos(contrato.tipos_cubiertos || []);
    setActivo(contrato.activo !== false);
    setObservacion(contrato.observacion || "");
  };

  const toggleTipo = (nombreTipo) => {
    setTiposCubiertos((prev) => {
      if (prev.includes(nombreTipo)) {
        return prev.filter((item) => item !== nombreTipo);
      }

      return [...prev, nombreTipo];
    });
  };

  const tiposCubiertosSinPrograma = tiposCubiertos.filter(
    (tipo) => !mantencionesPorTipo[tipo]
  );

  const guardarContrato = async () => {
    cambiarEstadoAlerta(false);
    cambiarAlerta({});

    if (!empresaId) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: "error",
        mensaje: "Debe seleccionar una empresa",
      });
      return;
    }

    if (activo && tiposCubiertos.length === 0) {
      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: "error",
        mensaje: "Debe seleccionar al menos un tipo cubierto",
      });
      return;
    }

    try {
      setGuardando(true);

      const contratoId = empresaId;

      await setDoc(
        doc(db, "contratos_mantencion", contratoId),
        {
          emp_id: empresaId,
          empresa: empresaSeleccionada?.empresa || "",

          actual: true,
          activo,

          cobertura_tipo: "TIPO",
          tipos_cubiertos: tiposCubiertos,

          st_destino_default: "LOGISTICMED_ST",

          tipos_sin_programa: tiposCubiertosSinPrograma,

          observacion: observacion.trim(),

          userMod: user?.email || "",
          fechaMod: serverTimestamp(),
        },
        { merge: true }
      );

      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: "exito",
        mensaje: "Contrato de mantención actualizado correctamente",
      });
    } catch (error) {
      console.error("Error guardando contrato:", error);

      cambiarEstadoAlerta(true);
      cambiarAlerta({
        tipo: "error",
        mensaje: "Error al guardar contrato de mantención",
      });
    } finally {
      setGuardando(false);
    }
  };

  if (loadingEmpresas) {
    return (
      <Container fluid style={{ padding: 24 }}>
        <Loader active inline="centered">
          Cargando empresas...
        </Loader>
      </Container>
    );
  }

  return (
    <Container fluid style={{ padding: 24 }}>
      <Segment
        raised
        style={{
          borderRadius: 18,
          border: "1px solid #e5e7eb",
          boxShadow: "0 14px 35px rgba(15, 23, 42, 0.08)",
          padding: 28,
        }}
      >
        <Header as="h2">
          <Icon name="file alternate outline" color="teal" />
          <Header.Content>
            Contrato Mantención
            <Header.Subheader>
              Administra qué tipos de equipos están cubiertos por contrato
            </Header.Subheader>
          </Header.Content>
        </Header>

        <Divider />

        <Grid stackable>
          <Grid.Column width={6}>
            <Form>
              <Form.Field>
                <label>Empresa cliente</label>
                <Dropdown
                  fluid
                  search
                  selection
                  placeholder="Seleccione empresa"
                  options={opcionesEmpresas}
                  value={empresaId}
                  onChange={(e, { value }) => setEmpresaId(value)}
                  disabled={loadingContrato || guardando}
                />
              </Form.Field>

              {empresaSeleccionada && (
                <Segment
                  basic
                  style={{
                    background: "#f8fafc",
                    borderRadius: 14,
                    padding: 16,
                    marginTop: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span>Empresa</span>
                    <b>{empresaSeleccionada.empresa}</b>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 8,
                    }}
                  >
                    <span>Contrato habilitado</span>
                    <Label
                      size="mini"
                      color={
                        empresaSeleccionada.contrato_mantencion
                          ? "green"
                          : "grey"
                      }
                    >
                      {empresaSeleccionada.contrato_mantencion ? "SÍ" : "NO"}
                    </Label>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Destino contrato</span>
                    <Label size="mini" color="teal">
                      LOGISTICMED ST
                    </Label>
                  </div>
                </Segment>
              )}

              <Segment
                style={{
                  borderRadius: 14,
                  border: "1px solid #ccfbf1",
                  background: "#f0fdfa",
                }}
              >
                <Header as="h4" color="teal">
                  <Icon name="settings" />
                  Reglas del contrato
                </Header>

                <Form.Checkbox
                  toggle
                  label="Contrato activo"
                  checked={activo}
                  onChange={(e, { checked }) => setActivo(checked)}
                  disabled={guardando}
                />

                <Form.TextArea
                  label="Observación"
                  placeholder="Ej: Contrato cubre mantención preventiva para los tipos seleccionados"
                  value={observacion}
                  onChange={(e) => setObservacion(e.target.value)}
                  disabled={guardando}
                />
              </Segment>

              <Segment
                basic
                style={{
                  background: "#f8fafc",
                  borderRadius: 14,
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 13, color: "#475569" }}>
                  <b>Regla operacional:</b> los tipos cubiertos por contrato
                  serán derivados por defecto a LogisticMedST. Los tipos no
                  cubiertos podrán enviarse a cualquier servicio técnico.
                </div>
              </Segment>
            </Form>
          </Grid.Column>

          <Grid.Column width={10}>
            <Segment
              style={{
                borderRadius: 14,
                border: "1px solid #e5e7eb",
              }}
            >
              <Header as="h3" color="blue">
                <Icon name="list" />
                Tipos cubiertos
              </Header>

              {loadingContrato ? (
                <Loader active inline="centered">
                  Cargando contrato...
                </Loader>
              ) : tipos.length === 0 ? (
                <Message warning>
                  Esta empresa no tiene tipos de equipos registrados.
                </Message>
              ) : (
                <>
                  {tiposCubiertosSinPrograma.length > 0 && (
                    <Message warning>
                      Hay tipos cubiertos que no tienen programa o mantenciones
                      activas:{" "}
                      <b>{tiposCubiertosSinPrograma.join(", ")}</b>.
                    </Message>
                  )}

                  <Message info>
                    Selecciona los tipos de equipos cubiertos por el contrato.
                    La columna “Programa” valida si existen mantenciones activas
                    para ese tipo.
                  </Message>

                  <Table celled selectable striped compact>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell width={2}>Cubierto</Table.HeaderCell>
                        <Table.HeaderCell width={4}>Familia</Table.HeaderCell>
                        <Table.HeaderCell>Tipo</Table.HeaderCell>
                        <Table.HeaderCell width={4}>Programa</Table.HeaderCell>
                        <Table.HeaderCell width={3}>Estado</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {tipos.map((tipoItem) => {
                        const nombreTipo =
                          tipoItem.tipo || tipoItem.nombre || tipoItem.id;

                        const nombreFamilia =
                          tipoItem.familia ||
                          tipoItem.familiar ||
                          "Sin familia";

                        const cubierta = tiposCubiertos.includes(nombreTipo);
                        const cubiertaOperativa = activo && cubierta;

                        const mantencionTipo =
                          mantencionesPorTipo[nombreTipo] || null;

                        const tienePrograma = Boolean(mantencionTipo);

                        return (
                          <Table.Row key={tipoItem.id}>
                            <Table.Cell textAlign="center">
                              <Form.Checkbox
                                checked={cubierta}
                                onChange={() => toggleTipo(nombreTipo)}
                                disabled={guardando || !activo}
                              />
                            </Table.Cell>

                            <Table.Cell>{nombreFamilia}</Table.Cell>

                            <Table.Cell>
                              <b>{nombreTipo}</b>
                            </Table.Cell>

                            <Table.Cell>
                              {tienePrograma ? (
                                <>
                                  <Label color="green" basic>
                                    OK
                                  </Label>
                                  <div
                                    style={{
                                      marginTop: 4,
                                      fontSize: 11,
                                      color: "#64748b",
                                    }}
                                  >
                                    {mantencionTipo.cantidad} equipo(s)
                                    {mantencionTipo.programas.length > 0
                                      ? ` - ${mantencionTipo.programas.join(
                                        ", "
                                      )}`
                                      : ""}
                                  </div>
                                </>
                              ) : (
                                <Label color={cubierta ? "orange" : "grey"} basic>
                                  {cubierta
                                    ? "SIN PROGRAMA"
                                    : "NO VALIDADO"}
                                </Label>
                              )}
                            </Table.Cell>

                            <Table.Cell>
                              <Label
                                color={
                                  !activo && cubierta
                                    ? "orange"
                                    : cubiertaOperativa
                                      ? "green"
                                      : "grey"
                                }
                                basic
                              >
                                {!activo && cubierta
                                  ? "CONTRATO INACTIVO"
                                  : cubiertaOperativa
                                    ? "CUBIERTO"
                                    : "NO CUBIERTO"}
                              </Label>
                            </Table.Cell>
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>

                  <div style={{ marginTop: 12 }}>
                    <Label color={activo ? "teal" : "orange"} basic>
                      {activo
                        ? `${tiposCubiertos.length} tipo(s) cubierto(s)`
                        : `${tiposCubiertos.length} tipo(s) configurado(s), contrato inactivo`}
                    </Label>

                    {tiposCubiertosSinPrograma.length > 0 && (
                      <Label color="orange" basic>
                        {tiposCubiertosSinPrograma.length} sin programa
                      </Label>
                    )}
                  </div>
                </>
              )}
            </Segment>
          </Grid.Column>
        </Grid>

        <Divider />

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <Button
            basic
            color="grey"
            type="button"
            disabled={guardando || loadingContrato}
            onClick={() => {
              cargarTipos(empresaId);
              cargarMantencionesActivas(empresaId);
              cargarContrato(empresaId);
            }}
          >
            <Icon name="refresh" />
            Recargar
          </Button>

          <Button
            color="teal"
            type="button"
            loading={guardando}
            disabled={guardando || loadingContrato}
            onClick={guardarContrato}
          >
            <Icon name="save outline" />
            Guardar contrato
          </Button>
        </div>
      </Segment>

      <Alerta
        tipo={alerta.tipo}
        mensaje={alerta.mensaje}
        estadoAlerta={estadoAlerta}
        cambiarEstadoAlerta={cambiarEstadoAlerta}
      />
    </Container>
  );
};

export default ContratoMantencion;